import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

export default function Privacy() {
  const updated = "October 2026";
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 starfield" data-testid="privacy-page">
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="font-serif text-lg text-slate-50">Cosmic Sage</span>
          </Link>
          <Link to="/dashboard" className="text-sm text-amber-300 hover:text-amber-200">← Back to App</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: {updated}</p>

        <section className="space-y-6 text-slate-300 leading-relaxed">
          <p>
            Cosmic Sage ("we", "us", "our") provides astrology, numerology and allied
            insights at <span className="text-amber-300">cosomicsage.com</span> and its sub-domains. This Privacy Policy
            explains what data we collect, why we collect it, and your rights over it.
          </p>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">1. Information We Collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>Birth details you enter</b>: date, time, and place of birth are used only to compute your charts, panchang, dasha, yogas and related insights. You can use the app anonymously — no account is required.</li>
            <li><b>Account data (optional)</b>: if you sign in with email/password or Google, we store your email, display name, and a hashed password. We never see or store your Google password.</li>
            <li><b>Usage analytics</b>: we may log anonymous page views, feature usage, and crash reports to improve the service. These logs do not identify you personally.</li>
            <li><b>Guest session identifier</b>: when you use the app without signing in, we mint a random anonymous ID (stored in your browser's localStorage) to track your free-action quota. This ID is not linked to any personal information.</li>
            <li><b>Palm reading images</b>: if you use the AI Palm Reading feature, your uploaded palm photo is sent to our AI partner for analysis and is deleted after the response is generated.</li>
          </ul>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To compute and display your astrology & numerology insights.</li>
            <li>To personalise AI Astro Chat replies by grounding them in your birth chart.</li>
            <li>To send push notifications about Ekadashi, Purnima, Amavasya (only if you opt in).</li>
            <li>To send transactional emails (password reset, security alerts). We do not send marketing emails without consent.</li>
            <li>To diagnose crashes and improve features.</li>
          </ul>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">3. Third-Party Services & Advertising</h2>
          <p>
            Cosmic Sage is free to use thanks to advertising. We use <b>Google AdSense</b> to serve ads
            on our website. Google and its partners may use cookies and similar technologies to serve
            ads based on your prior visits to this and other websites.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Google's use of advertising cookies enables it and its partners to serve ads based on
              your visits to our site and/or other sites on the Internet.
            </li>
            <li>
              You may opt out of personalised advertising by visiting{" "}
              <a href="https://www.google.com/settings/ads" className="text-amber-300 underline" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.
            </li>
            <li>
              Third-party vendors, including Google, use cookies to serve ads based on a user's
              previous visits to our website. You can read more at{" "}
              <a href="https://policies.google.com/technologies/partner-sites" className="text-amber-300 underline" target="_blank" rel="noopener noreferrer">how Google uses cookies in advertising</a>.
            </li>
            <li>
              AI features (Astro Chat, Palm Reading, Divisional Chart interpretation) are powered by
              our LLM provider (Anthropic / Google). Your questions and birth details may transit
              their systems; see their privacy policies for details.
            </li>
          </ul>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">4. Cookies & Local Storage</h2>
          <p>We use the following cookies / storage:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>Essential</b>: session token for signed-in users, guest ID for anonymous users, saved birth-profile cache.</li>
            <li><b>Analytics</b>: anonymous usage counters.</li>
            <li><b>Advertising</b>: Google AdSense cookies (served from <code className="text-xs bg-slate-900 px-1 py-0.5 rounded">pagead2.googlesyndication.com</code>) for ad personalisation and frequency capping.</li>
          </ul>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">5. Data Retention</h2>
          <p>
            Guest session data expires 90 days after your last visit. Signed-in account data is kept
            until you request deletion. Saved birth profiles and chat history are retained until you
            delete them from your profile page.
          </p>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">6. Your Rights</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access, correct, or export your data by emailing us at <span className="text-amber-300">support@cosomicsage.com</span>.</li>
            <li>Delete your account and all associated data at any time.</li>
            <li>Opt out of personalised advertising via Google Ads Settings.</li>
            <li>Disable push notifications from your browser settings.</li>
            <li>EU/UK/California residents: you have additional rights under GDPR / CCPA; contact us to exercise them.</li>
          </ul>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">7. Children</h2>
          <p>
            Cosmic Sage is not intended for children under 13 (or 16 in the EU). We do not knowingly
            collect data from children. If you believe a child has provided us with data, please
            contact us and we will delete it.
          </p>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">8. Security</h2>
          <p>
            We use HTTPS everywhere, hash passwords with bcrypt, and store data in access-controlled
            MongoDB clusters. No system is 100% secure — please use a strong unique password and
            report suspected breaches to <span className="text-amber-300">support@cosomicsage.com</span>.
          </p>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">9. Changes to this Policy</h2>
          <p>
            We may update this policy from time to time. The "Last updated" date reflects the latest
            revision. Material changes will be announced in-app.
          </p>

          <h2 className="font-serif text-2xl text-slate-50 pt-4">10. Contact</h2>
          <p>
            Questions? Reach out to <span className="text-amber-300">support@cosomicsage.com</span>.
          </p>
        </section>

        <footer className="mt-12 pt-6 border-t border-white/5 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/" className="hover:text-amber-300">Home</Link>
          <Link to="/terms" className="hover:text-amber-300">Terms of Service</Link>
          <Link to="/about" className="hover:text-amber-300">About</Link>
        </footer>
      </main>
    </div>
  );
}
